import { useContext } from 'react';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { SplitListPanelStoreContext } from '../context';
import { shallowEqual } from '../lib/equality';

import type {
  ListPanelActions,
  ListPanelDerivedState,
  ListPanelLayoutGuardState,
  ListPanelPreferenceState,
  ListPanelStore,
} from '../types';

/**
 * Hook to access the SplitListPanel context value from context.
 * Throws an error if used outside of SplitListPanelProvider.
 *
 * @example
 * ```tsx
 * const context = useSplitListPanelContext();
 * const state = context.store.getState();
 * ```
 */
export function useSplitListPanelContext() {
  const context = useContext(SplitListPanelStoreContext);

  if (!context) {
    throw new Error('useSplitListPanelContext must be used within a SplitListPanelProvider');
  }

  return context;
}

/**
 * Hook to subscribe to specific parts of the SplitListPanel store state.
 * Uses shallow equality by default to prevent unnecessary re-renders.
 *
 * @param selector - Function to select specific state
 * @param equalityFn - Optional custom equality function (defaults to shallow)
 *
 * @example
 * ```tsx
 * // Subscribe to selection count only
 * const count = useSplitListPanelSelector(state => state.selectionCount);
 *
 * // Subscribe to multiple values with custom equality
 * const { hasSelection, isSingleSelection } = useSplitListPanelSelector(
 *   state => ({
 *     hasSelection: state.hasSelection,
 *     isSingleSelection: state.isSingleSelection,
 *   })
 * );
 * ```
 */
export function useSplitListPanelSelector<T>(
  selector: (state: ListPanelStore) => T,
  equalityFn: (a: T, b: T) => boolean = shallowEqual as (a: T, b: T) => boolean,
): T {
  const context = useSplitListPanelContext();
  return useStoreWithEqualityFn(context.store, selector, equalityFn);
}

/**
 * Hook to get only the actions from the SplitListPanel store.
 * Actions are stable references and won't cause re-renders.
 *
 * @example
 * ```tsx
 * const actions = useSplitListPanelActions();
 *
 * const handleSelect = (ids: string[]) => {
 *   actions.pushSelection(ids, true);
 * };
 * ```
 */
export function useSplitListPanelActions(): ListPanelActions {
  const context = useSplitListPanelContext();

  return useStoreWithEqualityFn(
    context.store,
    state => ({
      clearSelection: state.clearSelection,
      pushSelection: state.pushSelection,
      reset: state.reset,
      setFocusedId: state.setFocusedId,
      setLayoutGuard: state.setLayoutGuard,
      setPreference: state.setPreference,
    }),
    () => true, // Actions are stable, never re-render
  );
}

/**
 * Hook to get derived state from the SplitPanel store.
 * Optimized to only re-render when derived values change.
 *
 * @example
 * ```tsx
 * const { hasSelection, selectionCount, isSingleSelection, isMultiSelection } = useSplitListPanelDerivedState();
 * ```
 */
export function useSplitListPanelDerivedState(): ListPanelDerivedState {
  return useSplitListPanelSelector(state => ({
    hasSelection: state.hasSelection,
    selectionCount: state.selectionCount,
    isSingleSelection: state.isSingleSelection,
    isMultiSelection: state.isMultiSelection,
  }));
}

/**
 * Hook to get preference state from the SplitPanel store.
 *
 * @example
 * ```tsx
 * const { enableMultiSelect, persistSelection } = useSplitListPanelStorePreferences();
 * ```
 */
export function useSplitListPanelStorePreferences(): ListPanelPreferenceState {
  return useSplitListPanelSelector(state => ({
    enableMultiSelect: state.enableMultiSelect,
    persistSelection: state.persistSelection,
  }));
}

/**
 * Hook to access panel preferences from bridge (enable, available).
 * These values come from the legacy panel system via adapter.
 *
 * Note: panelType and panelSize are DetailPanel-specific and not available for ListPanel.
 *
 * @example
 * ```tsx
 * const { enablePanel, availablePanel, setEnablePanel } = useSplitListPanelPreferences();
 * ```
 */
export function useSplitListPanelPreferences() {
  const context = useSplitListPanelContext();

  return {
    enablePanel: context.enablePanel,
    availablePanel: context.availablePanel,
    setEnablePanel: context.setEnablePanel,
  };
}

/**
 * Hook to get layout guard state from the SplitPanel store.
 *
 * @example
 * ```tsx
 * const { minPanelWidth, maxPanelWidth, defaultPanelWidth } = useSplitListPanelLayoutGuard();
 * ```
 */
export function useSplitListPanelLayoutGuard(): ListPanelLayoutGuardState {
  return useSplitListPanelSelector(state => ({
    minPanelWidth: state.minPanelWidth,
    maxPanelWidth: state.maxPanelWidth,
    defaultPanelWidth: state.defaultPanelWidth,
  }));
}

/**
 * Hook to get the selected IDs as an array.
 * Converts the internal Set to array for convenience.
 *
 * @example
 * ```tsx
 * const selectedIds = useSplitListPanelSelectedIds();
 * ```
 */
export function useSplitListPanelSelectedIds(): string[] {
  return useSplitListPanelSelector(
    state => Array.from(state.selectedIds),
    (a, b) => {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
      }
      return true;
    },
  );
}

/**
 * Hook to get the focused ID from the SplitPanel store.
 *
 * @example
 * ```tsx
 * const focusedId = useSplitListPanelFocusedId();
 * ```
 */
export function useSplitListPanelFocusedId(): string | null {
  return useSplitListPanelSelector(state => state.focusedId, Object.is);
}
